import React, { useEffect, useRef, useState } from 'react';
import { Popover, Button, CircularProgress } from '@mui/material';
import MonitoringInterval from 'js/components/data_sources/add_ds_form/MonitoringInterval/MonitoringInterval';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';
import { Field } from '@nexla/@types';
import classes from './CronScheduleDialog.module.scss';

const cronField: Field = {
  name: 'start.cron',
  label: 'Schedule Ingestion',
  display_name: 'Schedule Ingestion',
  inputType: 'text',
  required: false,
  key: 1,
  section: 'schedule',
  type: 'string',
};

// 'once' and undefined/missing are both "no recurring schedule"
const normalizeCron = (c: any): string | null => {
  const v = c?.['start.cron'];
  return !v || v === 'once' ? null : v;
};

// [], undefined, and missing are all "no triggers"; compare by resource id + event type
// (getTriggeringResourceId reads both the server and the picker-built trigger shapes).
const { getTriggeringResourceId: getTriggerResourceId } = FlowTriggerHelper;

const normalizeFlowTriggers = (c: any): string => {
  const triggers = c?.flow_triggers;
  if (!triggers || triggers.length === 0) return '';
  return JSON.stringify(
    [...triggers]
      .sort((a, b) => getTriggerResourceId(a) - getTriggerResourceId(b))
      .map((t) => ({
        resource_id: getTriggerResourceId(t),
        triggering_event_type: t.triggering_event_type,
      })),
  );
};

const hasScheduleChanged = (current: any, initial: any): boolean => {
  if (!initial) return false;
  return (
    normalizeCron(current) !== normalizeCron(initial) ||
    normalizeFlowTriggers(current) !== normalizeFlowTriggers(initial) ||
    !!current?.adaptive_flow !== !!initial?.adaptive_flow
  );
};

interface CronScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  config: any;
  onConfigChanged: (data: any, fieldName: string, value: any) => void;
  onUpdate: () => void;
  anchorEl: HTMLElement | null;
  isLoading?: boolean;
  source?: any;
  showExternalTrigger?: boolean;
}

const CronScheduleDialog = ({
  open,
  onClose,
  config,
  onConfigChanged,
  onUpdate,
  anchorEl,
  isLoading = false,
  source,
  showExternalTrigger = false,
}: CronScheduleDialogProps) => {
  const [userHasModified, setUserHasModified] = useState(false);
  const initialConfigRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      initialConfigRef.current = config;
    } else {
      setUserHasModified(false);
      initialConfigRef.current = null;
    }
    // config intentionally omitted: we want the snapshot pinned to the value at
    // dialog-open time, not re-captured on every external config change while open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'left',
      }}
      classes={{
        paper: classes.popoverPaper,
      }}
      style={{ zIndex: 1000 }}
    >
      <div className={classes.popoverContainer}>
        <MonitoringInterval
          field={cronField}
          config={config}
          onConfigChanged={onConfigChanged}
          onUserModified={() => setUserHasModified(true)}
          source={source}
          showExternalTrigger={showExternalTrigger}
        />
        <div className={classes.popoverActions}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>

          <span>
            <Button
              onClick={onUpdate}
              variant="contained"
              color="primary"
              disabled={
                isLoading ||
                !(userHasModified && hasScheduleChanged(config, initialConfigRef.current))
              }
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isLoading ? 'Updating...' : 'Update'}
            </Button>
          </span>
        </div>
      </div>
    </Popover>
  );
};

export default CronScheduleDialog;
